import {
  closeChatwoot,
  hideChatwoot,
  resetChatwoot,
  setChatwootCustomAttributes,
  setChatwootUser,
  showChatwoot,
  type ChatwootCustomerServiceProps,
} from '@iyb/business-components';

const CHATWOOT_BASE_URL = 'https://chatwoot.iswan.cc';
const CHATWOOT_WEBSITE_TOKEN = 'ojQEMFqWVsC61MucoZ9niVzL';

type CompanyData = Record<string, unknown>;
type ChatwootWindow = Window & {
  $chatwoot?: {
    hasLoaded?: boolean;
    toggle?: (state: 'open' | 'close') => void;
  };
};

interface UserInfo {
  identifier?: string | number;
  email?: string | null;
  phone?: string | null;
  avatar?: string | null;
  nickname?: string | null;
  company?: {
    name: string;
    data: CompanyData;
  };
  customAttributes?: Record<string, unknown>;
}

let pendingOpen = false;

const hasToken = () =>
  Boolean(CHATWOOT_WEBSITE_TOKEN) && !CHATWOOT_WEBSITE_TOKEN.includes('请替换为');

const warnUnavailable = () => {
  // if (process.env.NODE_ENV !== 'production') {
  //   console.warn('dev环境下不会自动加载客服模块，请在生产模式下使用');
  //   return;
  // }

  if (!hasToken()) {
    console.warn('Chatwoot 客服未配置 Website Token，请替换 ContactUtils 中的常量');
  }
};

const canUseContact = (options: { silent?: boolean } = {}) => {
  // if (process.env.NODE_ENV !== 'production') {
  //   if (!options.silent) {
  //     warnUnavailable();
  //   }
  //   return false;
  // }

  if (!hasToken()) {
    if (!options.silent) {
      warnUnavailable();
    }
    return false;
  }

  return true;
};

const getChatwoot = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return (window as ChatwootWindow).$chatwoot;
};

const isChatwootLoaded = () => Boolean(getChatwoot()?.hasLoaded);

const runChatwootAction = (
  actionName: string,
  action: () => void,
  options: { silent?: boolean } = {},
) => {
  try {
    action();
  } catch (error) {
    if (process.env.NODE_ENV !== 'production' && !options.silent) {
      console.warn(`[Chatwoot] ${actionName} 执行失败`, error);
    }
  }
};

const handleChatwootReady = () => {
  if (!pendingOpen) {
    runChatwootAction('hideChatwoot', hideChatwoot, { silent: true });
    return;
  }

  pendingOpen = false;
  runChatwootAction('showChatwoot', showChatwoot, { silent: true });
  runChatwootAction('openChatwoot', () => getChatwoot()?.toggle?.('open'));
};

const getContactOptions = (userInfo?: UserInfo): ChatwootCustomerServiceProps | undefined => {
  // if (process.env.NODE_ENV !== 'production') {
  //   return undefined;
  // }

  if (!hasToken()) {
    return undefined;
  }

  if (process.env.NODE_ENV !== 'production') {
    console.log('[Chatwoot] 使用 Website Token 初始化客服', {
      baseUrl: CHATWOOT_BASE_URL,
      tokenPrefix: CHATWOOT_WEBSITE_TOKEN.slice(0, 6),
    });
  }

  const customAttributes = {
    ...userInfo?.customAttributes,
    company: userInfo?.company?.name,
    ...(userInfo?.company?.data || {}),
  };

  return {
    websiteToken: CHATWOOT_WEBSITE_TOKEN,
    baseUrl: CHATWOOT_BASE_URL,
    locale: 'zh_CN',
    position: 'right',
    launcherTitle: 'open',
    hideMessageBubble: true,
    showUnreadMessagesDialog: false,
    onReady: handleChatwootReady,
    onLoadError: (error) => {
      console.error('[Chatwoot] 客服组件加载失败', error);
    },
    user:
      userInfo?.identifier !== undefined
        ? {
            identifier: userInfo.identifier,
            name: userInfo.nickname || undefined,
            email: userInfo.email || undefined,
            avatarUrl: userInfo.avatar || undefined,
            phoneNumber: userInfo.phone || undefined,
            customAttributes,
          }
        : undefined,
  };
};

const load = () => {
  canUseContact();
};

const showDialog = () => {
  if (!canUseContact()) return;

  runChatwootAction('showChatwoot', showChatwoot);
};

const hideDialog = () => {
  if (!canUseContact({ silent: true })) return;

  runChatwootAction('hideChatwoot', hideChatwoot, { silent: true });
};

const openDialog = () => {
  if (!canUseContact()) return;

  if (!isChatwootLoaded()) {
    pendingOpen = true;
  }

  runChatwootAction('showChatwoot', showChatwoot, { silent: true });
  runChatwootAction('openChatwoot', () => getChatwoot()?.toggle?.('open'));
};

const closeDialog = () => {
  if (!canUseContact({ silent: true })) return;

  runChatwootAction('closeChatwoot', closeChatwoot, { silent: true });
};

const clearSession = () => {
  if (!canUseContact({ silent: true })) return;

  runChatwootAction('resetChatwoot', resetChatwoot, { silent: true });
};

const getUnreadCount = () => {
  return 0;
};

const isInited = () => {
  return canUseContact({ silent: true }) && isChatwootLoaded();
};

const noop = () => undefined;

const setUserInfo = (userInfo: UserInfo) => {
  if (!canUseContact() || userInfo.identifier === undefined) return;

  runChatwootAction('setChatwootUser', () => {
    setChatwootUser({
      identifier: userInfo.identifier,
      name: userInfo.nickname || undefined,
      email: userInfo.email || undefined,
      avatarUrl: userInfo.avatar || undefined,
      phoneNumber: userInfo.phone || undefined,
      customAttributes: {
        ...userInfo.customAttributes,
        company: userInfo.company?.name,
        ...(userInfo.company?.data || {}),
      },
    });
  });
};

const setSession = (token: string) => {
  if (!canUseContact()) return;

  runChatwootAction('setChatwootCustomAttributes', () => {
    setChatwootCustomAttributes({ sessionToken: token });
  });
};

export const ContactUtils = {
  init: load,
  load,
  showDialog,
  hideDialog,
  openDialog,
  closeDialog,
  setSession,
  clearSession,
  getUnreadCount,
  isInited,
  onChatClose: noop,
  offChatClose: noop,
  onLoaded: noop,
  offLoaded: noop,
  setUserInfo,
  getContactOptions,
};
